import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Lightbulb, CheckCircle2, AlertTriangle, FileDown, ChevronDown, ChevronRight } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import axios from "axios";
import { useAuth, isAdmin } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });

const TRAITS = [
  { key: "ownership",                   label: "Ownership",                     category: "Core Values" },
  { key: "discipline",                  label: "Discipline",                    category: "Core Values" },
  { key: "respect",                     label: "Respect",                       category: "Core Values" },
  { key: "growth_mindset",              label: "Growth Mindset",                category: "Core Values" },
  { key: "service",                     label: "Service",                       category: "Core Values" },
  { key: "attitude_energy",             label: "Attitude / Energy",             category: "Presence" },
  { key: "communication_body_language", label: "Communication / Body Language", category: "Presence" },
  { key: "focus_engagement",            label: "Focus / Engagement",            category: "Presence" },
  { key: "competitiveness",             label: "Competitiveness",               category: "Motor" },
  { key: "resilience",                  label: "Resilience",                    category: "Motor" },
  { key: "effort_fight",                label: "Effort / Fight",                category: "Motor" },
  { key: "work_rate",                   label: "Work Rate",                     category: "Motor" },
  { key: "reliability_trust",           label: "Reliability / Trust",           category: "Team Impact" },
  { key: "coachability_accountability", label: "Coachability / Accountability", category: "Team Impact" },
  { key: "sacrifice",                   label: "Sacrifice",                     category: "Team Impact" },
];
const CHARACTER_CATEGORIES = ["Core Values", "Presence", "Motor", "Team Impact"];
export const CHARACTER_DNA = { title: "Character DNA", apiPath: "character-dna", traits: TRAITS, categories: CHARACTER_CATEGORIES };

// Generic trait assessment (Anchor / Good / Rate Limiter). Defaults to Character DNA; Physicality / Technical / Tactical pass their own config.
export default function CharacterDNAAssessment({ playerId, playerName, onAssessmentsChanged, config = CHARACTER_DNA, compact = false, legacy = false }) {
  const { title, apiPath, traits: TRAITS, categories: CATEGORIES } = config;
  const labelFor = (k) => TRAITS.find(t => t.key === k)?.label || k;
  const { user } = useAuth();
  const canEdit = isAdmin(user) && !legacy;
  const [assessments, setAssessments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [ratings, setRatings] = useState({});
  const [raterName, setRaterName] = useState("");
  const [raterRole, setRaterRole] = useState("coach");
  const [label, setLabel] = useState("");
  const [exporting, setExporting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const printRef = useRef(null);

  const notify = (list) => { if (typeof onAssessmentsChanged === "function") onAssessmentsChanged(list); };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await client.get(`/players/${playerId}/${apiPath}`);
        const list = r.data.assessments || [];
        setAssessments(list);
        if (list.length) setSelected(list[0]);
        notify(list);
      } catch { toast.error("Load failed"); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const setRating = (key, val) => setRatings(r => ({ ...r, [key]: { ...(r[key] || {}), rating: val } }));
  const setNote   = (key, val) => setRatings(r => ({ ...r, [key]: { ...(r[key] || {}), context_note: val } }));

  const submit = async () => {
    const rated = Object.entries(ratings).filter(([, v]) => v?.rating);
    if (rated.length === 0) { toast.error("Rate at least one trait"); return; }
    setCreating(true);
    try {
      const r = await client.post(`/players/${playerId}/${apiPath}`, {
        label: label.trim() || null,
        rater: { name: raterName.trim() || null, role: raterRole },
        ratings,
      });
      const nextList = [r.data, ...assessments];
      setAssessments(nextList);
      setSelected(r.data);
      setShowForm(false);
      setExpanded(true);
      setRatings({}); setLabel(""); setRaterName("");
      notify(nextList);
      toast.success("Assessment saved");
    } catch (e) { toast.error("Save failed", { description: e?.response?.data?.detail || e.message }); }
    finally { setCreating(false); }
  };

  const updatePicks = async (field, keys) => {
    if (!selected) return;
    const updated = { ...selected, [field]: keys };
    setSelected(updated);
    const nextList = assessments.map(x => x.id === selected.id ? updated : x);
    setAssessments(nextList);
    notify(nextList);
    try { await client.patch(`/players/${playerId}/${apiPath}/${selected.id}`, { [field]: keys }); }
    catch { toast.error("Save failed"); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.label || 'Assessment'}"?`)) return;
    try {
      await client.delete(`/players/${playerId}/${apiPath}/${a.id}`);
      const nextList = assessments.filter(x => x.id !== a.id);
      setAssessments(nextList);
      if (selected?.id === a.id) setSelected(nextList[0] || null);
      notify(nextList);
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const exportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(printRef.current, { backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2;
      // Scale-to-fit ONE page (contain).
      const scale = Math.min(maxW / canvas.width, maxH / canvas.height);
      const finalW = canvas.width * scale;
      const finalH = canvas.height * scale;
      pdf.addImage(img, "PNG", (pageW - finalW) / 2, (pageH - finalH) / 2, finalW, finalH);
      const safe = (playerName || "Player").replace(/\s+/g, "-");
      pdf.save(`${safe}-Character-DNA-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); }
    finally { setExporting(false); }
  };

  return (
    <section className="border border-white/10 bg-[#141414] p-6" data-testid={`assessment-${apiPath.split("/").pop()}`}>
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="font-heading uppercase text-lg tracking-[0.2em] text-white">{title} · {TRAITS.length}-Trait Assessment</h3>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] mt-1">{apiPath === "character-dna" ? "Human ratings · AI narrative via Claude Sonnet 5" : "Anchor · Good · Rate Limiter — Anchors and Rate Limiters roll up into the Assessment Review"}</div>
        </div>
        <div className="flex gap-2">
          {canEdit && !showForm && (
            <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-3 py-1.5 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold" data-testid="cdna-new-btn">
              <Plus className="w-3 h-3" /> New Assessment
            </button>
          )}
          {selected && expanded && (
            <button onClick={exportPDF} disabled={exporting} className="flex items-center gap-2 px-3 py-1.5 border border-white/20 text-white text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-60" data-testid="cdna-pdf-btn">
              <FileDown className="w-3 h-3" /> {exporting ? "Exporting…" : "Share as PDF"}
            </button>
          )}
          {assessments.length > 1 && (
            <select value={selected?.id || ""} onChange={e => setSelected(assessments.find(a => a.id === e.target.value))}
              className="bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-xs text-white">
              {assessments.map(a => <option key={a.id} value={a.id}>{a.label || "Assessment"} · {(a.created_at || "").slice(0, 10)}</option>)}
            </select>
          )}
        </div>
      </div>

      {showForm && (
        <div className="border border-[var(--org-accent)]/40 bg-[#101010] p-4 mb-4" data-testid="cdna-form">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label (optional)"
              className="bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white" data-testid="cdna-label" />
            <input value={raterName} onChange={e => setRaterName(e.target.value)} placeholder="Rater name"
              className="bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white" data-testid="cdna-rater-name" />
            <select value={raterRole} onChange={e => setRaterRole(e.target.value)}
              className="bg-[#0f0f0f] border border-white/10 px-3 py-2 text-sm text-white" data-testid="cdna-rater-role">
              <option value="coach">Coach</option><option value="trainer">Trainer</option><option value="parent">Parent</option>
            </select>
          </div>
          {CATEGORIES.map(cat => (
            <div key={cat} className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)] mb-2">{cat}</div>
              <div className="space-y-2">
                {TRAITS.filter(t => t.category === cat).map(t => {
                  const cur = ratings[t.key] || {};
                  return (
                    <div key={t.key} className="border border-white/5 bg-[#141414] p-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 text-sm text-white font-bold">{t.label}</div>
                        <div className="flex gap-1">
                          <button onClick={() => setRating(t.key, cur.rating === "anchor" ? null : "anchor")}
                            className={`px-3 py-1 text-[10px] font-bold tracking-[0.15em] border ${cur.rating === "anchor" ? "bg-[#065f46] text-white border-[#065f46]" : "text-[#a0a0a0] border-white/15 hover:text-white"}`}
                            data-testid={`cdna-${t.key}-anchor`}>ANCHOR</button>
                          <button onClick={() => setRating(t.key, cur.rating === "solid" ? null : "solid")}
                            className={`px-3 py-1 text-[10px] font-bold tracking-[0.15em] border ${cur.rating === "solid" ? "bg-[#3f3f46] text-white border-[#3f3f46]" : "text-[#a0a0a0] border-white/15 hover:text-white"}`}
                            data-testid={`cdna-${t.key}-solid`}>GOOD</button>
                          <button onClick={() => setRating(t.key, cur.rating === "needs_work" ? null : "needs_work")}
                            className={`px-3 py-1 text-[10px] font-bold tracking-[0.15em] border ${cur.rating === "needs_work" ? "bg-[#7f1d1d] text-white border-[#7f1d1d]" : "text-[#a0a0a0] border-white/15 hover:text-white"}`}
                            data-testid={`cdna-${t.key}-needs`}>RATE LIMITER</button>
                        </div>
                      </div>
                      {cur.rating === "needs_work" && (
                        <textarea value={cur.context_note || ""} onChange={e => setNote(t.key, e.target.value)}
                          placeholder="Optional: describe the pattern or a specific incident..."
                          rows={2} className="mt-2 w-full bg-[#0f0f0f] border border-white/10 px-2 py-1.5 text-[13px] text-white"
                          data-testid={`cdna-${t.key}-note`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setShowForm(false); setRatings({}); }} className="px-4 py-2 border border-white/15 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold" data-testid="cdna-cancel">Cancel</button>
            <button onClick={submit} disabled={creating} className="flex items-center gap-2 px-5 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold disabled:opacity-60" data-testid="cdna-submit">
              {creating ? <><Loader2 className="w-3 h-3 animate-spin" /> Analyzing…</> : "Run Assessment"}
            </button>
          </div>
        </div>
      )}

      {loading && <div className="text-[#757575] text-xs flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> loading…</div>}
      {!loading && !selected && !showForm && (
        <div className="text-[#757575] text-sm py-4">{canEdit ? (apiPath === "character-dna" ? "Run a new assessment to see the AI report." : "Run a new assessment to rate each trait.") : "No assessment yet."}</div>
      )}
      {selected && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-between gap-3 border border-white/10 bg-[#101010] hover:bg-[#282828] px-4 py-3 text-left"
          data-testid="cdna-expand-btn"
        >
          <div className="flex items-center gap-3 min-w-0">
            <ChevronRight className="w-4 h-4 text-[var(--org-accent)] shrink-0" />
            <div className="min-w-0">
              <div className="text-[13px] text-white font-bold truncate">
                {selected.label || "Assessment"}
                {selected.rater?.name ? <span className="text-[#a0a0a0] font-normal"> · {selected.rater.name}</span> : null}
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#757575] mt-0.5">
                {(selected.created_at || "").slice(0, 10)}
                {(() => {
                  const anchors = Object.values(selected.ratings || {}).filter(r => r?.rating === "anchor").length;
                  const solid = Object.values(selected.ratings || {}).filter(r => r?.rating === "solid").length;
                  const needs = Object.values(selected.ratings || {}).filter(r => r?.rating === "needs_work").length;
                  return <span> · <span className="text-[#4ade80]">{anchors} Anchors</span> · <span className="text-[#c0c0c0]">{solid} Good</span> · <span className="text-[#f87171]">{needs} Rate Limiters</span></span>;
                })()}
              </div>
            </div>
          </div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] font-bold shrink-0">View Report</span>
        </button>
      )}
      {selected && expanded && (
        <>
          <div className="flex justify-end mb-2">
            <button
              onClick={() => setExpanded(false)}
              className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0] hover:text-white"
              data-testid="cdna-collapse-btn"
            >
              <ChevronDown className="w-3 h-3" /> Collapse
            </button>
          </div>
          <div ref={printRef}>
            <AssessmentReport a={selected} playerName={playerName} canEdit={canEdit} onUpdate={updatePicks} onDelete={() => remove(selected)} config={config} />
          </div>
        </>
      )}
    </section>
  );
}

function AssessmentReport({ a, playerName, canEdit, onUpdate, onDelete, config }) {
  const { title, traits: TRAITS, categories: CATEGORIES } = config;
  const labelFor = (k) => TRAITS.find(t => t.key === k)?.label || k;
  const nar = a.narrative || {};
  const tf = nar.trait_feedback || {};
  const ratedKeys = Object.keys(a.ratings || {}).filter(k => a.ratings[k]?.rating);
  const eliteAvail = ratedKeys.filter(k => a.ratings[k]?.rating === "anchor" || a.ratings[k]?.rating === "solid");
  const needsAvail = ratedKeys.filter(k => a.ratings[k]?.rating === "needs_work");
  const togglePick = (field, key) => {
    const cur = a[field] || [];
    const next = cur.includes(key) ? cur.filter(x => x !== key) : (cur.length >= 3 ? [...cur.slice(1), key] : [...cur, key]);
    onUpdate(field, next);
  };
  return (
    <div data-testid="cdna-report" className="space-y-4 bg-[#141414] p-2">
      {/* Print header (visible in PDF) */}
      <div className="border-b border-white/10 pb-3 mb-2">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#a0a0a0]">{title} · Assessment</div>
        <div className="font-heading text-xl text-white uppercase tracking-tight mt-1">{playerName || "Player"}</div>
        <div className="text-[11px] text-[#a0a0a0] mt-1">
          {a.label ? <span className="text-white">{a.label} · </span> : null}
          {(a.created_at || "").slice(0, 10)}
          {a.rater?.name ? <span> · Rated by {a.rater.name}{a.rater.role ? ` (${a.rater.role})` : ""}</span> : (a.rater?.role ? <span> · Rater: {a.rater.role}</span> : null)}
        </div>
      </div>
      {/* Curated picks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PickPanel labelFor={labelFor} title="Anchors · 3 Points of Emphasis" color="#065f46" available={eliteAvail} picked={a.elite_traits || []} onToggle={(k) => canEdit && togglePick("elite_traits", k)} canEdit={canEdit} testId="cdna-elite" />
        <PickPanel labelFor={labelFor} title="Rate Limiters · 3 Points of Emphasis" color="#7f1d1d" available={needsAvail} picked={a.needs_work_traits || []} onToggle={(k) => canEdit && togglePick("needs_work_traits", k)} canEdit={canEdit} testId="cdna-needs" />
      </div>
      {/* Headline */}
      {nar.headline && (
        <div className="border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/5 p-4">
          <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-[var(--org-accent)]" />
            <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)]">Coach's Headline</div></div>
          <div className="text-[14px] text-white leading-relaxed">{nar.headline}</div>
        </div>
      )}
      {/* Trait Breakdown */}
      <div className="space-y-2">
        {CATEGORIES.map(cat => {
          const list = TRAITS.filter(t => t.category === cat && a.ratings?.[t.key]?.rating);
          if (!list.length) return null;
          return (
            <div key={cat}>
              <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-[var(--org-accent)] mb-2 mt-3">{cat}</div>
              <div className="space-y-2">
                {list.map(t => {
                  const r = a.ratings[t.key];
                  const fb = tf[t.key] || {};
                  const solid = r.rating !== "needs_work";
                  const badge = r.rating === "anchor" ? ["ANCHOR", "#065f46"] : r.rating === "solid" ? ["GOOD", "#3f3f46"] : ["RATE LIMITER", "#7f1d1d"];
                  return (
                    <div key={t.key} className="border border-white/10 bg-[#101010] p-3">
                      <div className="flex items-center gap-3 mb-1">
                        {solid ? <CheckCircle2 className="w-4 h-4 text-[#4ade80] shrink-0" /> : <AlertTriangle className="w-4 h-4 text-[#f87171] shrink-0" />}
                        <div className="font-bold text-white text-[14px] flex-1">{t.label}</div>
                        <span className="text-[9px] font-bold tracking-[0.2em] px-2 py-1" style={{ background: badge[1], color: "#fff" }}>
                          {badge[0]}
                        </span>
                      </div>
                      {r.context_note && <div className="text-[11px] text-[#c0c0c0] italic border-l-2 border-[var(--org-accent)]/40 pl-2 mb-2">"{r.context_note}"</div>}
                      {fb.what_it_means && <div className="text-[13px] text-white leading-relaxed">{fb.what_it_means}</div>}
                      {fb.why_it_matters && <div className="text-[12px] text-[#a0a0a0] mt-1"><span className="uppercase tracking-[0.2em] text-[9px] font-bold">Why it matters · </span>{fb.why_it_matters}</div>}
                      {fb.how_to_build && !solid && <div className="text-[12px] text-white mt-2 border-l-2 border-[var(--org-accent)] pl-3"><span className="uppercase tracking-[0.2em] text-[9px] font-bold text-[var(--org-accent)]">How to build · </span>{fb.how_to_build}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      {canEdit && (
        <div className="flex justify-end pt-2">
          <button onClick={onDelete} className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#757575] hover:text-red-300"><Trash2 className="w-3 h-3" /> Delete assessment</button>
        </div>
      )}
    </div>
  );
}

export function PickPanel({ labelFor, title, color, available, picked, onToggle, canEdit, testId, emptyText }) {
  return (
    <div className="border border-white/10 bg-[#101010] p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.4em] font-bold mb-2" style={{ color }}>{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {available.length === 0 && <div className="text-[11px] text-[#757575]">{emptyText || `No ${title.startsWith("Anchors") ? "Anchor / Good" : "Rate Limiter"} traits rated yet.`}</div>}
        {available.map(k => {
          const active = picked.includes(k);
          return (
            <button key={k} disabled={!canEdit} onClick={() => onToggle(k)}
              className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 border font-bold ${active ? "text-white" : "text-[#a0a0a0] border-white/15 hover:text-white"}`}
              style={active ? { background: color, borderColor: color } : {}}
              data-testid={`${testId}-${k}`}>
              {labelFor(k)}
            </button>
          );
        })}
      </div>
      {canEdit && picked.length > 0 && <div className="text-[9px] uppercase tracking-[0.3em] text-[#757575] mt-2">{picked.length}/4 selected</div>}
    </div>
  );
}
